import { getAuthConfig } from '../config/auth';
import { buildApiUrl } from '../config/api';

/**
 * Decodifica il payload di un JWT (parte centrale, base64url).
 * Restituisce l'oggetto payload o null in caso di errore.
 */
export const decodeJwtPayload = (token) => {
  if (!token || typeof token !== 'string') return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64url = parts[1];
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

/**
 * Recupera il JWT dall'endpoint accounts (DEV/PROD).
 * Restituisce il token string o null se assente/errore.
 */
export const fetchJwtToken = async (jwtUrl) => {
  try {
    const res = await fetch(jwtUrl, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.JwtToken ?? null;
  } catch {
    return null;
  }
};

/**
 * Recupera dal backend se il check JWT è richiesto (App:RequireJwtAuth).
 * In caso di errore restituisce true (richiedi auth per sicurezza).
 */
export const getRequireJwtAuth = async () => {
  try {
    const url = buildApiUrl('import-excel/auth-config');
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) return true;
    const data = await res.json();
    return data?.requireJwtAuth === true;
  } catch {
    return true;
  }
};

/**
 * Verifica se l'email è presente in app.usersForImport (endpoint can-import).
 */
export const isEmailAllowedForImport = async (email) => {
  if (!email || typeof email !== 'string') return false;
  try {
    const url = `${buildApiUrl('import-excel/can-import')}?email=${encodeURIComponent(email.trim())}`;
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) return false;
    const data = await res.json();
    return data?.canImport === true;
  } catch {
    return false;
  }
};

/**
 * Esegue il flusso di auth: ottiene JWT, estrae UserName (email), verifica in usersForImport.
 * Se tutto ok restituisce true; se non autorizzato reindirizza alla pagina 401 e restituisce false.
 */
export const runAuthCheck = async () => {
  const { isLocal, jwtUrl, unauthorizedRedirectUrl } = getAuthConfig();

  if (isLocal) return true;

  if (!jwtUrl || !unauthorizedRedirectUrl) {
    window.location.href = unauthorizedRedirectUrl || 'about:blank';
    return false;
  }

  const token = await fetchJwtToken(jwtUrl);
  if (!token) {
    window.location.href = unauthorizedRedirectUrl;
    return false;
  }

  const payload = decodeJwtPayload(token);
  const userName = payload?.UserName ?? payload?.userName;
  if (!userName || typeof userName !== 'string') {
    window.location.href = unauthorizedRedirectUrl;
    return false;
  }

  const allowed = await isEmailAllowedForImport(userName);
  if (!allowed) {
    window.location.href = unauthorizedRedirectUrl;
    return false;
  }

  return true;
};
